import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  increment,
} from "firebase/firestore";
import { db } from "./firebase";

/**
 * @typedef {Object} TenantContext
 * @property {string}  uid       - Firebase Auth UID of the current user.
 * @property {Object}  userData  - Merged user doc + bands map.
 * @property {string|null} bandId    - Active band ID, or null when the user has no bands.
 * @property {string}  bandName  - Display name of the active band.
 * @property {"admin"|"member"} role - Caller's membership role in the active band.
 * @property {boolean} isOwner   - True only when the caller is the band's ownerId.
 */

const defaultBandContext = {
  bandId: null,
  bandName: "",
  role: "member",
  isOwner: false,
};

const hasBandsMap = (userData) => {
  return !!(
    userData?.bands &&
    typeof userData.bands === "object" &&
    Object.keys(userData.bands).length > 0
  );
};

const readUserDocData = async (uid) => {
  const userSnap = await getDoc(doc(db, "users", uid));
  return userSnap.exists() ? userSnap.data() : {};
};

const readUserBandsSubcollection = async (uid) => {
  const bands = {};
  const bandsSnap = await getDocs(collection(db, "users", uid, "bands"));

  bandsSnap.forEach((bandDoc) => {
    bands[bandDoc.id] = bandDoc.data();
  });

  return bands;
};

const deriveBandsFromMembership = async (uid, candidateBands = {}) => {
  const entries = Object.entries(candidateBands || {});

  const results = await Promise.all(
    entries.map(async ([bandId, bandInfo]) => {
      const memberSnap = await getDoc(doc(db, "bands", bandId, "members", uid));
      if (!memberSnap.exists()) return null;

      const memberData = memberSnap.data();
      let bandName = bandInfo?.bandName || "";

      if (!bandName) {
        try {
          const bandSnap = await getDoc(doc(db, "bands", bandId));
          if (bandSnap.exists()) {
            bandName = bandSnap.data().name || "";
          }
        } catch {
          // Keep cached name if the band root doc is temporarily unreadable.
        }
      }

      const role = memberData.role || "member";

      // Cache write — fire-and-forget, not correctness-critical.
      setDoc(
        doc(db, "users", uid, "bands", bandId),
        { role, bandName },
        { merge: true }
      ).catch(() => {});

      return [bandId, { role, bandName }];
    })
  );

  return Object.fromEntries(results.filter(Boolean));
};

const backfillOwnerMemberships = async (uid, cachedBands = {}, memberships = {}) => {
  const updates = { ...memberships };

  const toBackfill = Object.entries(cachedBands || {}).filter(
    ([bandId, bandInfo]) => bandInfo?.owned && !updates[bandId]
  );

  await Promise.all(
    toBackfill.map(async ([bandId, bandInfo]) => {
      try {
        // Owner self-enrollment is allowed by rules when band.ownerId == uid.
        await setDoc(
          doc(db, "bands", bandId, "members", uid),
          { role: "admin" },
          { merge: true }
        );

        updates[bandId] = {
          role: "admin",
          bandName: bandInfo?.bandName || "",
        };

        await setDoc(
          doc(db, "users", uid, "bands", bandId),
          { role: "admin", bandName: bandInfo?.bandName || "", owned: true },
          { merge: true }
        );
      } catch {
        // Ignore bands that fail owner backfill checks.
      }
    })
  );

  return updates;
};

const pickActiveBand = (bandsMap, preferredBandId = null) => {
  const entries = Object.entries(bandsMap || {});
  if (entries.length === 0) return defaultBandContext;

  if (preferredBandId && bandsMap[preferredBandId]) {
    const preferredBand = bandsMap[preferredBandId];
    return {
      bandId: preferredBandId,
      bandName: preferredBand?.bandName || "",
      role: preferredBand?.role || "member",
      isOwner: false, // resolved to real value by resolveTenantContext
    };
  }

  const [bandId, bandInfo] = entries[0];
  return {
    bandId,
    bandName: bandInfo?.bandName || "",
    role: bandInfo?.role || "member",
    isOwner: false, // resolved to real value by resolveTenantContext
  };
};

export const resolveTenantContext = async (uid, seedUserData = null) => {
  let userData = seedUserData || (await readUserDocData(uid));
  const cachedBandsMap = hasBandsMap(userData)
    ? userData.bands
    : await readUserBandsSubcollection(uid);

  const candidateBands = { ...cachedBandsMap };
  if (userData?.lastActiveBandId && !candidateBands[userData.lastActiveBandId]) {
    candidateBands[userData.lastActiveBandId] = {};
  }

  // Membership docs are the security-rule authority for tenant access.
  let bandsMap = await deriveBandsFromMembership(uid, candidateBands);
  bandsMap = await backfillOwnerMemberships(uid, cachedBandsMap, bandsMap);

  if (Object.keys(bandsMap).length === 0) bandsMap = {};

  const activeBand = pickActiveBand(bandsMap, userData?.lastActiveBandId || null);

  if (activeBand.bandId && userData?.lastActiveBandId !== activeBand.bandId) {
    await setDoc(
      doc(db, "users", uid),
      { lastActiveBandId: activeBand.bandId },
      { merge: true }
    );
  }

  userData = {
    ...userData,
    bands: bandsMap,
    lastActiveBandId: activeBand.bandId || userData?.lastActiveBandId || null,
  };

  let isOwner = false;
  if (activeBand.bandId) {
    try {
      const bandSnap = await getDoc(doc(db, "bands", activeBand.bandId));
      isOwner = (bandSnap.data()?.ownerId ?? null) === uid;
    } catch {
      // Treat unreadable band doc as non-owner; access control is enforced server-side.
    }
  }

  return {
    uid,
    userData,
    ...activeBand,
    isOwner,
  };
};

export const setLastActiveBandId = async (uid, bandId) => {
  if (!uid || !bandId) return;
  await setDoc(doc(db, "users", uid), { lastActiveBandId: bandId }, { merge: true });
};

export const requireTenantBandContext = (tenantContext) => {
  if (!tenantContext?.bandId) {
    throw new Error("Tenant context is not resolved yet");
  }
  return tenantContext;
};

// Firestore: bands/{bandId}/sheet_music
export const tenantSheetMusicCollectionRef = (tenantContext) => {
  const tenant = requireTenantBandContext(tenantContext);
  return collection(db, "bands", tenant.bandId, "sheet_music");
};

export const tenantSheetMusicDocRef = (tenantContext, songId) => {
  const tenant = requireTenantBandContext(tenantContext);
  return doc(db, "bands", tenant.bandId, "sheet_music", songId);
};

// Storage: bands/{bandId}/{folder}/{fileName}
// folder should be "sheet_music" for chart PDFs, "setlists" for exports.
export const tenantStoragePath = (tenantContext, folder, fileName) => {
  const tenant = requireTenantBandContext(tenantContext);
  return `bands/${tenant.bandId}/${folder}/${fileName}`;
};

// Firestore: bands/{bandId}/setlists
export const tenantSetlistsCollectionRef = (tenantContext) => {
  const tenant = requireTenantBandContext(tenantContext);
  return collection(db, "bands", tenant.bandId, "setlists");
};

export const tenantSetlistDocRef = (tenantContext, setlistId) => {
  const tenant = requireTenantBandContext(tenantContext);
  return doc(db, "bands", tenant.bandId, "setlists", setlistId);
};

// Firestore: bands/{bandId}/members
export const tenantMembersCollectionRef = (tenantContext) => {
  const tenant = requireTenantBandContext(tenantContext);
  return collection(db, "bands", tenant.bandId, "members");
};

// Firestore: bands/{bandId}/sheet_music/{songId}/notes
export const tenantNotesCollectionRef = (tenantContext, songId) => {
  const tenant = requireTenantBandContext(tenantContext);
  return collection(db, "bands", tenant.bandId, "sheet_music", songId, "notes");
};

export const tenantNoteDocRef = (tenantContext, songId, noteId) => {
  const tenant = requireTenantBandContext(tenantContext);
  return doc(db, "bands", tenant.bandId, "sheet_music", songId, "notes", noteId);
};

export const incrementSongNoteCount = (tenantContext, songId, delta) => {
  const tenant = requireTenantBandContext(tenantContext);
  return updateDoc(
    doc(db, "bands", tenant.bandId, "sheet_music", songId),
    { noteCount: increment(delta) }
  );
};
